import { useEffect, useMemo, useState } from 'react'
import cn from 'classnames'
import { getCarouselImagesForAnimationLoop, isLoadingCarouselImages } from '../../selectors/carouselImages'
import { useDispatch, useSelector } from 'react-redux'
import { isFeatureOn } from '../../VNConfigurations/Selectors'
import { VNCONFIGS_FEATURE_ENABLEMENT_KEYS } from '../../VNConfigurations/Enums'
import { getAttractLoopImages, getCarouselImages } from '../../actions/carouselImages'
import KioskPrompt from './KioskPrompt'
import ModalMessage from './ModalMessage'
import ManagedImage from '../components/Image'
import styles from './Slideshow.module.scss'
import VNCachedImage from '../../VNImageCache/components/VNCachedImage'
import { getKioskOfflineEnabled } from '../utils/config'
import { ATTRACT_LOOP_CACHE_CATEGORY, getCachedImagesKeys } from '../../VNImageCache/utils'
import { getNetworkAvailable } from '../../selectors/config'

const CAROUSEL_SLIDE_INTERVAL = 6_000 // 6 seconds
const SLIDES_BUFFER_INTERVAL = 400 // 0.4 second overlap for fade effect

/**
 * Slideshow component for displaying carousel images.
 * Supports both legacy and new attract loop images based on feature flag.
 * @returns {JSX.Element}
 */
const Slideshow = () => {
  const dispatch = useDispatch()
  const [currentIndex, setCurrentIndex] = useState(0)
  const [currentOfflineIndex, setCurrentOfflineIndex] = useState(0)
  const isLoadingImages = useSelector(isLoadingCarouselImages)
  const hasAttractLoopFeature = useSelector((state) =>
    isFeatureOn(state, VNCONFIGS_FEATURE_ENABLEMENT_KEYS.KIOSK_ATTRACT_LOOP_IMAGE),
  )
  const loadedImages = useSelector(getCarouselImagesForAnimationLoop)
  const isOnline = useSelector(getNetworkAvailable)
  const [cachedImagesKeys, setCachedImagesKeys] = useState([])
  const kioskOfflineEnabled = getKioskOfflineEnabled({ fromAttractLoop: true })
  const networkAvailable = useSelector(getNetworkAvailable)
  const isLoading = useMemo(() => {
    if (!networkAvailable && kioskOfflineEnabled) {
      return false
    }
    return isLoadingImages
  }, [isLoadingImages, kioskOfflineEnabled, networkAvailable])

  useEffect(() => {
    // temporary guard against fetching images when offline
    if (!isOnline) return

    // legacy manually set slideshow images
    if (!hasAttractLoopFeature) {
      dispatch(getCarouselImages())
      return
    }

    dispatch(getAttractLoopImages())
  }, [dispatch, hasAttractLoopFeature, isOnline])

  useEffect(() => {
    if (!hasAttractLoopFeature || !kioskOfflineEnabled || !loadedImages.length) {
      return
    }
    getCachedImagesKeys(ATTRACT_LOOP_CACHE_CATEGORY).then((keys) => {
      setCachedImagesKeys(keys)
    })
  }, [loadedImages, hasAttractLoopFeature, kioskOfflineEnabled, isOnline])

  // Auto-advance slideshow with overlapping fade effect
  useEffect(() => {
    if (loadedImages.length <= 1) return
    const timer = setInterval(() => {
      setTimeout(() => {
        setCurrentIndex((i) => (i + 1) % loadedImages.length)
      }, SLIDES_BUFFER_INTERVAL)
    }, CAROUSEL_SLIDE_INTERVAL)
    return () => clearInterval(timer)
  }, [loadedImages])

  // Auto-advance slideshow with overlapping fade effect for cached images
  useEffect(() => {
    if (cachedImagesKeys.length <= 1) return

    const timer = setInterval(() => {
      setTimeout(() => {
        setCurrentOfflineIndex((i) => (i + 1) % cachedImagesKeys.length)
      }, SLIDES_BUFFER_INTERVAL)
    }, CAROUSEL_SLIDE_INTERVAL)

    return () => clearInterval(timer)
  }, [cachedImagesKeys])

  if (isLoading) {
    return <ModalMessage state="Loading" />
  }

  if (!loadedImages.length || (!isOnline && kioskOfflineEnabled && !cachedImagesKeys.length)) {
    return <KioskPrompt />
  }

  if (!isOnline && kioskOfflineEnabled) {
    return (
      <div className={styles.container}>
        {cachedImagesKeys.map((imageKey, index) => {
          const isCurrent = index === currentOfflineIndex
          return (
            <VNCachedImage
              imageKey={imageKey}
              alt=""
              className={cn(
                styles.slide,
                isCurrent ? styles.fadeIn : styles.fadeOut,
                isCurrent ? styles.current : styles.previous,
              )}
            />
          )
        })}
      </div>
    )
  }

  return (
    <div className={styles.container}>
      {loadedImages.map((url, index) => {
        const isCurrent = index === currentIndex
        return (
          <ManagedImage
            key={index}
            url={url}
            alt=""
            className={cn(styles.slide, isCurrent ? styles.fadeIn : styles.fadeOut, isCurrent ? styles.current : styles.previous)}
            noCache
          />
        )
      })}
    </div>
  )
}

export default Slideshow
